import * as turf from '@turf/turf';

import { GeoLocation } from '@features/components/visualization/mapbox/models/map-data';
import {
  decodePolyline,
  encodePolyline
} from '@features/components/visualization/mapbox/utils/another-polyline-decoder';
import { FlexiblePolylineDecoder } from '@features/components/visualization/mapbox/utils/flexible-polyline-decoder';

export type PolylineFormat = 'Polyline5' | 'FlexPolyline';

export interface ArcEndpoints {
  tail: GeoLocation;
  head: GeoLocation;
}

/**
 * `undecodable` is reported by the field, not by `validatePath`: it is the state of a
 * non-empty stored string that decoded to nothing, which a decoded path can no longer
 * express. Only the flexible-polyline codec can reach it — polyline5 never fails, it just
 * yields nonsense, which `endpoints-too-far` catches instead.
 */
export type PolylineProblem = 'undecodable' | 'too-few-points' | 'endpoints-too-far';

/**
 * Mirrors the data layer's POLYLINE_ENDPOINT_TOLERANCE_METERS. The backend rejects a
 * polyline whose ends are further than this from the sites the arc names; checking it here
 * turns a rejected save into an inline message.
 */
export const ENDPOINT_TOLERANCE_METERS = 500;

/**
 * A polyline is stored exactly as authored, so the declared format — not a guess — chooses
 * the codec. The two codecs fail in opposite ways, so neither is safe to guess with: a
 * flexible polyline read as polyline5 decodes to plausible-looking coordinates in the wrong
 * hemisphere, since the polyline5 codec never throws, while polyline5 read as a flexible
 * polyline throws. Hence no fallback.
 */
export function decodePath(
  encoded: string | null | undefined,
  format: PolylineFormat
): GeoLocation[] {
  if (!encoded) {
    return [];
  }
  try {
    const points =
      format === 'FlexPolyline'
        ? FlexiblePolylineDecoder.decode(encoded)
        : decodePolyline(encoded);
    return points.map(({ lat, lng }) => ({ lat, lng }));
  } catch {
    return [];
  }
}

/** The front end authors polyline5 only. */
export function encodePath(path: GeoLocation[]): string {
  return encodePolyline(path);
}

export function pathLengthKm(path: GeoLocation[]): number {
  if (path.length < 2) {
    return 0;
  }
  return turf.length(turf.lineString(path.map(({ lat, lng }) => [lng, lat])), {
    units: 'kilometers'
  });
}

function metersBetween(a: GeoLocation, b: GeoLocation): number {
  return turf.distance([a.lng, a.lat], [b.lng, b.lat], { units: 'meters' });
}

/**
 * Mirrors `ringToSvgPath`: one scale for both axes so the shape is not stretched, centred in
 * the box, and inset by `padding` so a stroke on the outermost vertex is not half-clipped —
 * which is what a due north–south arc, whose every vertex shares one longitude, would be.
 */
export function pathToSvgPath(
  path: GeoLocation[],
  width: number,
  height: number,
  padding = 2
): string {
  if (path.length === 0) {
    return '';
  }
  const lats = path.map((p) => p.lat);
  const lngs = path.map((p) => p.lng);
  const minLng = Math.min(...lngs);
  const maxLat = Math.max(...lats);
  const spanLng = Math.max(...lngs) - minLng || 1;
  const spanLat = maxLat - Math.min(...lats) || 1;
  const innerWidth = Math.max(width - padding * 2, 0);
  const innerHeight = Math.max(height - padding * 2, 0);
  const scale = Math.min(innerWidth / spanLng, innerHeight / spanLat);
  const offsetX = padding + (innerWidth - spanLng * scale) / 2;
  const offsetY = padding + (innerHeight - spanLat * scale) / 2;

  return path
    .map((point, index) => {
      const x = offsetX + (point.lng - minLng) * scale;
      // SVG's y axis runs opposite to latitude, so north maps to the top.
      const y = offsetY + (maxLat - point.lat) * scale;
      return `${index === 0 ? 'M' : 'L'} ${x.toFixed(2)} ${y.toFixed(2)}`;
    })
    .join(' ');
}

/**
 * Emptiness is deliberately not a problem: the generic required validator every field gets
 * already owns it, and reporting it here paints an untouched optional field red.
 */
export function validatePath(
  path: GeoLocation[],
  endpoints: ArcEndpoints | null
): PolylineProblem | null {
  if (path.length === 0) {
    return null;
  }
  if (path.length < 2) {
    return 'too-few-points';
  }
  if (!endpoints) {
    return null;
  }
  const startsFar = metersBetween(path[0], endpoints.tail) > ENDPOINT_TOLERANCE_METERS;
  const endsFar =
    metersBetween(path[path.length - 1], endpoints.head) > ENDPOINT_TOLERANCE_METERS;
  return startsFar || endsFar ? 'endpoints-too-far' : null;
}

/**
 * Index of the path vertex closest to `point`, or `-1` when there is no path at all.
 * Callers must read that sentinel as ordering before every real index, not as index 0.
 */
export function nearestPathIndex(path: GeoLocation[], point: GeoLocation): number {
  let bestIndex = -1;
  let bestDistance = Number.POSITIVE_INFINITY;
  path.forEach((candidate, index) => {
    const distance = metersBetween(candidate, point);
    if (distance < bestDistance) {
      bestDistance = distance;
      bestIndex = index;
    }
  });
  return bestIndex;
}

/**
 * Where a new via belongs in the ordered via list. Vias are ordered along the path, so the
 * answer is how many existing vias sit before the grabbed point. Appending blindly would
 * re-route tail → later via → earlier via → head, producing a detour that looks like a
 * provider fault rather than a front-end one.
 */
export function viaInsertionIndex(
  path: GeoLocation[],
  vias: GeoLocation[],
  grabbedIndex: number
): number {
  return vias.filter((via) => nearestPathIndex(path, via) < grabbedIndex).length;
}
